/* ==========================================================================
   Fast Header / Menus (BirCata)
   - rAF + cached metrics
   - passive listeners
   - throttled hover
   - minimal layout thrash
========================================================================== */
(function () {
  'use strict';

  var prefersReduced =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* -----------------------------
     Utils
  ------------------------------ */
  function on(el, ev, fn, opts) {
    if (!el) return;
    el.addEventListener(ev, fn, Object.assign({ passive: true }, opts || {}));
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments, self = this;
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  var rAF = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };

  /* -----------------------------
     BasicHeader
  ------------------------------ */
  class BasicHeader extends HTMLElement {
    constructor() {
      super();
      this._resizeRO = null;
      this._updateHeights = this.setHeight.bind(this);
    }

    get headerSection() { return document.querySelector('.header-section'); }
    get headerNavigation() { return this.querySelector('.header__bottom'); }
    get navigationToggleButton() { return this.querySelector('.toggle-navigation-button'); }
    get enableTransparent() { return this.dataset.enableTransparent === 'true'; }

    connectedCallback() {
      this.init();

      if ('ResizeObserver' in window) {
        var debounced = debounce(this._updateHeights, 120);
        this._resizeRO = new ResizeObserver(debounced);
        this._resizeRO.observe(this);
      }

      if (window.Shopify && Shopify.designMode) {
        var section = this.closest('.shopify-section');
        ['shopify:section:load','shopify:section:unload','shopify:section:reorder']
          .forEach((e) => on(section, e, this.init.bind(this)));
      }
    }

    disconnectedCallback() {
      if (this._resizeRO) this._resizeRO.disconnect();
    }

    init() {
      if (window.FoxTheme && FoxTheme.delayUntilInteraction) {
        new FoxTheme.delayUntilInteraction(this.setHeight.bind(this));
      } else {
        this.setHeight();
      }

      if (this.enableTransparent && this.headerSection) {
        this.headerSection.classList.add('header-transparent');
      }
    }

    calculateHeaderGroupHeight() {
      var headerGroups = document.querySelectorAll('.shopify-section-group-header-group');
      var total = 0;
      for (var i = 0; i < headerGroups.length; i++) total += headerGroups[i].offsetHeight;
      document.documentElement.style.setProperty('--header-group-height', total + 'px');
      return total;
    }

    setHeight() {
      var self = this;
      rAF(function () {
        var nav = self.headerNavigation;
        var offsetHeight = Math.round(self.offsetHeight || 0);
        var offsetTop = Math.round((self.parentElement && self.parentElement.offsetTop) || 0);
        var navH = Math.round((nav && nav.offsetHeight) || 0);

        var root = document.documentElement.style;
        root.setProperty('--header-height', offsetHeight + 'px');
        root.setProperty('--header-offset-top', offsetTop + 'px');
        root.setProperty('--header-navigation-height', navH + 'px');

        self.calculateHeaderGroupHeight();
      });
    }
  }
  customElements.define('basic-header', BasicHeader, { extends: 'header' });

  /* -----------------------------
     StickyHeader
  ------------------------------ */
  class StickyHeader extends BasicHeader {
    constructor() {
      super();

      this.classes = {
        pinned: 'header-pinned',
        headerScrolled: 'header-scrolled',
        show: 'is-show',
        hide: 'is-hide',
        headerSticky: 'header-sticky',
        isHideNav: 'is-hide-nav'
      };

      this.navigationManuallyToggled = false;

      this._lastY = window.scrollY || 0;
      this._scrollDir = 'none';
      this._scrollDist = 0;
      this._tick = false;

      this.scrollThreshold = 200;

      this.headerBoundsTop = 0;
      this.headerBoundsBottom = 0;

      this._onScroll = this._onScroll.bind(this);
      this._updateMetrics = this._updateMetrics.bind(this);
    }

    get isAlwaysSticky() { return this.dataset.stickyType === 'always'; }
    get collapseOnScroll() { return this.dataset.collapseOnScroll === 'true'; }

    connectedCallback() {
      super.connectedCallback();

      this._updateMetrics();
      this.initStickyHeader();

      if (this.collapseOnScroll && this.navigationToggleButton) {
        on(this.navigationToggleButton, 'click', this.handleNavigationToggle.bind(this), { passive: false });
      }

      if ('ResizeObserver' in window && this.headerSection) {
        this._boundsRO = new ResizeObserver(debounce(this._updateMetrics, 120));
        this._boundsRO.observe(this.headerSection);
      } else {
        on(window, 'resize', debounce(this._updateMetrics, 120));
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      if (this._boundsRO) this._boundsRO.disconnect();
    }

    _updateMetrics() {
      var rect = this.headerSection ? this.headerSection.getBoundingClientRect() : { top: 0, bottom: 0 };
      var pageY = window.scrollY || 0;
      this.headerBoundsTop = rect.top + pageY;
      this.headerBoundsBottom = rect.bottom + pageY;
      this.setHeight();
    }

    initStickyHeader() {
      if (!this.headerSection) return;
      this.headerSection.classList.add(this.classes.headerSticky);
      this.headerSection.dataset.stickyType = this.dataset.stickyType;
      window.addEventListener('scroll', this._onScroll, { passive: true });
    }

    handleNavigationToggle(e) {
      e.preventDefault();
      this.navigationManuallyToggled = true;

      var nav = this.headerNavigation;
      if (!nav) return;

      var isHidden = nav.classList.contains(this.classes.hide);

      rAF(() => {
        nav.classList.toggle(this.classes.hide, !isHidden);
        nav.classList.toggle(this.classes.show, isHidden);
        document.body.classList.toggle(this.classes.isHideNav, !isHidden);
        if (this.navigationToggleButton) this.navigationToggleButton.setAttribute('aria-expanded', String(isHidden));
      });

      setTimeout(() => { this.navigationManuallyToggled = false; }, 600);
    }

    _onScroll() {
      if (this._tick) return;
      this._tick = true;

      rAF(() => {
        var y = window.scrollY || 0;

        var dir = y > this._lastY ? 'down' : 'up';
        if (dir !== this._scrollDir) {
          this._scrollDist = 0;
          this._scrollDir = dir;
        } else {
          this._scrollDist += Math.abs(y - this._lastY);
        }

        var pastTop = y > this.headerBoundsTop;

        if (pastTop) {
          this._handleAfterHeader(y);
        } else {
          this._handleBeforeHeader();
        }

        this._lastY = y;
        this._tick = false;
      });
    }

    _handleAfterHeader(y) {
      var cls = this.classes;

      if (!this.headerSection.classList.contains(cls.headerScrolled)) {
        this.headerSection.classList.add(cls.headerScrolled);
      }

      if (this.collapseOnScroll) {
        if (this.navigationToggleButton) this.navigationToggleButton.classList.add(cls.show);
        if (!this.navigationManuallyToggled && !this.headerNavigation.classList.contains(cls.show)) {
          this.headerNavigation.classList.add(cls.hide);
          document.body.classList.add(cls.isHideNav);
        }
      }

      if (this.isAlwaysSticky) {
        document.body.classList.add(cls.pinned);
        return;
      }

      var isUp = this._scrollDir === 'up';
      var nearHeader = y < (this.headerBoundsBottom + 100);
      var movedEnough = this._scrollDist >= this.scrollThreshold;

      if (isUp || nearHeader) {
        document.body.classList.add(cls.pinned);
      } else if (!this.navigationManuallyToggled && movedEnough) {
        document.body.classList.remove(cls.pinned);
      }
    }

    _handleBeforeHeader() {
      var cls = this.classes;

      this.headerSection.classList.remove(cls.headerScrolled);

      if (this.collapseOnScroll) {
        if (!this.navigationManuallyToggled) {
          document.body.classList.remove(cls.isHideNav);
          if (this.headerNavigation) this.headerNavigation.classList.remove(cls.hide, cls.show);
          if (this.navigationToggleButton) this.navigationToggleButton.setAttribute('aria-expanded', 'false');
        }
        if (this.navigationToggleButton) this.navigationToggleButton.classList.remove(cls.show);
      }

      if (this.isAlwaysSticky) {
        document.body.classList.remove(cls.pinned);
      }
    }
  }
  customElements.define('sticky-header', StickyHeader, { extends: 'header' });

  /* -----------------------------
     DetailsDropdown (+ Mega)
  ------------------------------ */
  const clearDropdownCount = new WeakMap();

  class DetailsDropdown extends HTMLDetailsElement {
    constructor() {
      super();

      this.classes = { bodyClass: 'has-dropdown-menu' };
      this.events = { handleAfterHide: 'menu:handleAfterHide', handleAfterShow: 'menu:handleAfterShow' };

      this.summaryElement = this.firstElementChild;
      this.contentElement = this.lastElementChild;

      this._open = this.hasAttribute('open');

      on(this.summaryElement, 'click', this.handleSummaryClick.bind(this), { passive: false });

      // hover (desktop only)
      if (this.trigger === 'hover') {
        on(this.summaryElement, 'focusin', (e) => { if (e.target === this.summaryElement) this.open = true; });
        on(this.summaryElement, 'focusout', (e) => { if (!this.contentElement.contains(e.relatedTarget)) this.open = false; });
      }

      this.handleOutsideClick = this.handleOutsideClick.bind(this);
      this.handleEscKeyPress = this.handleEscKeyPress.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);

      // throttled hover using pointer events
      this._hoverInTimer = null;
      this._hoverOutTimer = null;

      on(this, 'pointerenter', () => {
        if (this.trigger !== 'hover') return;
        clearTimeout(this._hoverOutTimer);
        this._hoverInTimer = setTimeout(() => { this.open = true; }, 90);
      });

      on(this, 'pointerleave', () => {
        if (this.trigger !== 'hover') return;
        clearTimeout(this._hoverInTimer);
        this._hoverOutTimer = setTimeout(() => { this.open = false; }, 120);
      });
    }

    set open(v) {
      if (v === this._open) return;
      this._open = v;
      if (this.isConnected) {
        this.transition(v);
      } else {
        v ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }
    get open() { return this._open; }

    get trigger() {
      if (window.FoxTheme && FoxTheme.config && FoxTheme.config.isTouch) return 'click';
      return this.getAttribute('trigger') || 'click';
    }
    get level() { return this.getAttribute('level') || 'top'; }

    handleSummaryClick(e) {
      e.preventDefault();
      if (!(window.FoxTheme && FoxTheme.config && FoxTheme.config.isTouch) && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link')) {
        window.location.href = this.summaryElement.getAttribute('data-link');
      } else {
        this.open = !this.open;
      }
    }

    async transition(value) {
      if (value) {
        clearDropdownCount.set(DetailsDropdown, (clearDropdownCount.get(DetailsDropdown) || 0) + 1);
        document.body.classList.add(this.classes.bodyClass);
        this.setAttribute('open', '');
        this.summaryElement.setAttribute('open', '');

        setTimeout(() => { this.contentElement.setAttribute('open', ''); }, 60);

        document.addEventListener('click', this.handleOutsideClick, { passive: true });
        document.addEventListener('keydown', this.handleEscKeyPress, { passive: true });
        document.addEventListener('focusout', this.handleFocusOut, { passive: true });

        if (window.FoxTheme && FoxTheme.Motion && !prefersReduced) {
          await this.showWithTransition();
        }

        var MenuProductList = this.querySelector('menu-product-list');
        if (MenuProductList && typeof MenuProductList === 'object') MenuProductList.calcNavButtonsPosition();

        this.needsReverse();

        return (window.FoxTheme && FoxTheme.utils && FoxTheme.utils.waitForEvent)
          ? FoxTheme.utils.waitForEvent(this, this.events.handleAfterShow)
          : Promise.resolve();
      } else {
        clearDropdownCount.set(DetailsDropdown, (clearDropdownCount.get(DetailsDropdown) || 1) - 1);
        document.body.classList.toggle(this.classes.bodyClass, (clearDropdownCount.get(DetailsDropdown) || 0) > 0);

        this.summaryElement.removeAttribute('open');
        this.contentElement.removeAttribute('open');
        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('keydown', this.handleEscKeyPress);
        document.removeEventListener('focusout', this.handleFocusOut);

        if (window.FoxTheme && FoxTheme.Motion && !prefersReduced) {
          await this.hideWithTransition();
        }

        if (!this.open) this.removeAttribute('open');

        return (window.FoxTheme && FoxTheme.utils && FoxTheme.utils.waitForEvent)
          ? FoxTheme.utils.waitForEvent(this, this.events.handleAfterHide)
          : Promise.resolve();
      }
    }

    async showWithTransition() {
      FoxTheme.Motion.animate(this.contentElement, { opacity: [0, 1], visibility: 'visible' }, { duration: 0.5, easing: [0.7, 0, 0.2, 1], delay: 0.05 });
      var ty = this.level === 'top' ? '-105%' : '2rem';
      return FoxTheme.Motion.animate(this.contentElement.firstElementChild, { transform: ['translateY(' + ty + ')', 'translateY(0)'] }, { duration: 0.45, easing: [0.7, 0, 0.2, 1] }).finished;
    }

    async hideWithTransition() {
      FoxTheme.Motion.animate(this.contentElement, { opacity: 0, visibility: 'hidden' }, { duration: 0.25, easing: [0.7, 0, 0.2, 1] });
      var ty = this.level === 'top' ? '-105%' : '2rem';
      return FoxTheme.Motion.animate(this.contentElement.firstElementChild, { transform: 'translateY(' + ty + ')' }, { duration: 0.4, easing: [0.7, 0, 0.2, 1] }).finished;
    }

    handleOutsideClick(e) {
      var inside = this.contains(e.target);
      var onDetails = e.target.closest('details') instanceof DetailsDropdown;
      if (!inside && !onDetails) this.open = false;
    }

    handleEscKeyPress(e) {
      if (e.code === 'Escape') {
        var targetMenu = e.target.closest('details[open]');
        if (targetMenu) targetMenu.open = false;
      }
    }

    handleFocusOut(e) {
      if (e.relatedTarget && !this.contains(e.relatedTarget)) this.open = false;
    }

    needsReverse() {
      var rect = this.contentElement.getBoundingClientRect();
      if (rect.right > window.innerWidth) {
        this.contentElement.classList.add('needs-reverse');
      } else {
        this.contentElement.classList.remove('needs-reverse');
      }
    }
  }
  customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
  clearDropdownCount.set(DetailsDropdown, 0);

  class DetailsMega extends DetailsDropdown {
    constructor() {
      super();
      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', () => { this.open = true; });
        this.addEventListener('shopify:block:deselect', () => { this.open = false; });
      }
    }
    async showWithTransition() {
      if (!(window.FoxTheme && FoxTheme.Motion) || prefersReduced) return Promise.resolve();
      return FoxTheme.Motion.animate(
        this.contentElement.firstElementChild,
        { visibility: 'visible', transform: ['translateY(-100%)', 'translateY(0)'] },
        { duration: 0.45, easing: [0.39, 0.575, 0.565, 1.0] }
      ).finished;
    }
    async hideWithTransition() {
      if (!(window.FoxTheme && FoxTheme.Motion) || prefersReduced) {
        if (this.contentElement.firstElementChild) this.contentElement.firstElementChild.style.visibility = 'hidden';
        return Promise.resolve();
      }
      return FoxTheme.Motion.animate(
        this.contentElement.firstElementChild,
        { visibility: 'hidden', transform: 'translateY(-100%)' },
        { duration: 0.45, easing: [0.39, 0.575, 0.565, 1.0] }
      ).finished;
    }
  }
  customElements.define('details-mega', DetailsMega, { extends: 'details' });

  /* -----------------------------
     MenuDrawerDetails & Drawer
  ------------------------------ */
  class MenuDrawerDetails extends HTMLDetailsElement {
    constructor() {
      super();
      on(this.summary, 'click', this.onSummaryClick.bind(this), { passive: false });
      on(this.closeButton, 'click', this.onCloseButtonClick.bind(this), { passive: true });
    }
    get parent() { return this.closest('[data-parent]'); }
    get summary() { return this.querySelector('summary'); }
    get closeButton() { return this.querySelector('button'); }

    onSummaryClick(e) {
      e.preventDefault();
      setTimeout(() => {
        if (this.parent) this.parent.classList.add('active');
        this.classList.add('active');
        this.summary.setAttribute('aria-expanded', 'true');
      }, 80);
    }

    onCloseButtonClick() {
      if (this.parent) this.parent.classList.remove('active');
      this.classList.remove('active');
      this.summary.setAttribute('aria-expanded', 'false');
      this.closeAnimation();
    }

    closeAnimation() {
      var start;
      var self = this;
      function tick(t) {
        if (start === undefined) start = t;
        if ((t - start) < 400) rAF(tick);
        else self.removeAttribute('open');
      }
      rAF(tick);
    }
  }
  customElements.define('menu-drawer-details', MenuDrawerDetails, { extends: 'details' });

  class MenuDrawer extends DrawerComponent {
    constructor() { super(); }
    get header() { return this.closest('header'); }
    get requiresBodyAppended() { return false; }

    prepareToShow() {
      super.prepareToShow();

      var headerRect = this.header ? this.header.getBoundingClientRect() : { bottom: 0 };
      var root = document.documentElement.style;
      root.setProperty('--viewport-height', window.innerHeight + 'px');
      root.setProperty('--header-bottom-position', parseInt(headerRect.bottom, 10) + 'px');

      document.dispatchEvent(new CustomEvent('menu-drawer:open', { bubbles: true }));
    }

    handleAfterHide() {
      super.handleAfterHide();
      setTimeout(() => {
        this.querySelectorAll('details[is=menu-drawer-details]').forEach(function (menu) {
          menu.onCloseButtonClick();
        });
      }, 0);
    }
  }
  customElements.define('menu-drawer', MenuDrawer);

  /* -----------------------------
     MenuProductList
  ------------------------------ */
  class MenuProductList extends HTMLElement {
    constructor() {
      super();
      this.initSlide();
      this.elementToFocus = this.querySelector('button');
    }

    get container() { return this.querySelector('.swiper'); }
    get sliderPagination() { return this.querySelector('.swiper-pagination'); }
    get sliderNext() { return this.querySelector('.swiper-button-next'); }
    get sliderPrev() { return this.querySelector('.swiper-button-prev'); }
    get numberOfColumns() { return parseInt(this.dataset.columns || '2', 10); }

    initSlide() {
      var slider = new FoxTheme.Carousel(this.container, {
        spaceBetween: 10,
        slidesPerView: this.numberOfColumns,
        loop: false,
        navigation: { nextEl: this.sliderNext, prevEl: this.sliderPrev },
        focusableElements: 'input, select, option, textarea, button, video, label, a'
      });
      slider.init();

      var focusables = FoxTheme.a11y.getFocusableElements(this);
      focusables.forEach(function (el) {
        el.addEventListener('focusin', function () {
          var slide = el.closest('.swiper-slide');
          if (slide) slider.slider.slideTo(slider.slider.slides.indexOf(slide));
        }, { passive: true });
      });
    }

    calcNavButtonsPosition() {
      if (this.dataset.calcButtonPosition !== 'true') return;
      var firstMedia = this.querySelector('.product-card__image-wrapper');
      if (firstMedia && firstMedia.clientHeight > 0) {
        this.style.setProperty('--swiper-navigation-top-offset', (parseInt(firstMedia.clientHeight, 10) / 2) + 'px');
      }
    }
  }
  customElements.define('menu-product-list', MenuProductList);

  /* -----------------------------
     MenuSidebar
  ------------------------------ */
  class MenuSidebar extends HTMLElement {
    constructor() {
      super();
      this.handleSidenavMenuToggle = this.handleSidenavMenuToggle.bind(this);
      this.updateHeight = this.updateHeight.bind(this);
    }

    get summarys() { return this.querySelectorAll('summary'); }

    connectedCallback() {
      this.setInitialMinHeight();

      var first = this.summarys[0];
      if (first) this.setActiveItem(first, false);

      this.summarys.forEach((summary) => {
        on(summary, 'mouseenter', this.handleSidenavMenuToggle);
        on(summary, 'click', (e) => e.preventDefault(), { passive: false });
      });

      var io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.isIntersecting) this.updateHeight(); });
      });
      io.observe(this);
      this._io = io;
    }

    disconnectedCallback() {
      this.summarys.forEach((summary) => {
        summary.removeEventListener('mouseenter', this.handleSidenavMenuToggle);
      });
      if (this._io) this._io.disconnect();
    }

    setInitialMinHeight() {
      var container = this.closest('.mega-menu__wrapper');
      if (container) {
        var parentH = container.parentElement ? container.parentElement.offsetHeight : 0;
        container.style.setProperty('--menu-sidebar-height', parentH + 'px');
      }
    }

    updateHeight() {
      var active = this.querySelector('.is-visible');
      if (!active) return;
      var container = this.closest('.mega-menu__wrapper');
      var content = active.nextElementSibling;
      if (container && content) {
        container.style.setProperty('--sidebar-height', content.scrollHeight + 'px');
      }
    }

    setActiveItem(summaryElem, doUpdate = true) {
      var last = this.querySelector('.is-visible');
      if (last) last.classList.remove('is-visible');
      summaryElem.classList.add('is-visible');
      if (doUpdate) this.updateHeight();
    }

    handleSidenavMenuToggle(evt) {
      this.setActiveItem(evt.currentTarget);
    }
  }
  customElements.define('menu-sidebar', MenuSidebar);

})();
